import { motion } from 'framer-motion';
import { Check, MessageCircle } from 'lucide-react';

const Consultas = () => {
  const fadeUp = {
    initial: { opacity: 0, y: 40 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.8, ease: 'easeOut' },
  };

  const services = [
    {
      title: 'Acompanhamento Nutricional',
      price: 'R$ 500',
      period: 'Pagamento único',
      image: 'https://images.unsplash.com/photo-1758652561808-a0d9337fd57a?w=800&q=85',
      features: [
        'Avaliação física completa',
        'Bioimpedância',
        'Estratégia nutricional personalizada',
        'Ajuste de macros',
        'Estratégia para emagrecimento ou hipertrofia',
        'Suporte inicial',
      ],
    },
    {
      title: 'Acompanhamento Trimestral',
      price: 'R$ 1.200',
      period: '3 meses',
      image: 'https://images.unsplash.com/photo-1502783167913-8e718e555a06?w=800&q=85',
      featured: true,
      features: [
        'Consulta + avaliação corporal',
        'Plano alimentar individualizado',
        'Reavaliação mensal (3x)',
        'Suporte via WhatsApp',
      ],
    },
    {
      title: 'Acompanhamento Semestral',
      price: 'R$ 2.100',
      period: '6 meses',
      image: 'https://images.unsplash.com/photo-1699214493925-54c7e2fc8ef6?w=800&q=85',
      features: [
        'Tudo do Trimestral',
        'Reavaliações quinzenais',
        'Acesso prioritário e suporte estendido',
        'Dossiê de evolução semestral',
      ],
    },
  ];

  return (
    <div className="min-h-screen" data-testid="consultas-page">
      {/* Hero Section */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
          >
            <h1 className="text-4xl md:text-6xl font-bold tracking-tight uppercase mb-6">
              Protocolos & Serviços
            </h1>
            <p className="text-base md:text-lg font-light leading-relaxed tracking-wide text-[#A3A3A3] max-w-3xl mx-auto">
              Escolha o plano ideal para sua transformação. Cada serviço é desenvolvido com foco em
              resultados reais e sustentáveis.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section data-editor-key="services" className="pb-24 md:pb-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className={`grid grid-cols-1 gap-8 ${
            services.length === 1 ? 'max-w-md mx-auto' :
            services.length === 2 ? 'lg:grid-cols-2 max-w-4xl mx-auto' :
            services.length === 3 ? 'lg:grid-cols-3' :
            'sm:grid-cols-2 lg:grid-cols-4'
          }`}>
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                {...fadeUp}
                transition={{ delay: index * 0.1 }}
                className={`flex flex-col p-8 bg-[#121212] border transition-all duration-300 relative ${
                  service.featured
                    ? 'border-white/30 lg:-mt-8 lg:mb-0 lg:scale-105'
                    : 'border-white/10 hover:border-white/20'
                }`}
                data-testid={`pricing-card-${index}`}
              >
                {service.featured && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-[#C0C0C0] text-black px-4 py-1 text-xs font-bold tracking-widest uppercase">
                    Mais Popular
                  </div>
                )}

                <div className="relative h-48 -mx-8 -mt-8 mb-8 overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#121212] to-transparent" />
                </div>

                <h3 className="text-2xl font-bold tracking-tight mb-2">{service.title}</h3>
                <div className="mb-6">
                  <div className="text-4xl font-black tracking-tighter text-[#C0C0C0] mb-1">
                    {service.price}
                  </div>
                  <div className="text-sm text-[#A3A3A3] tracking-wide">{service.period}</div>
                </div>

                <div className="flex-grow mb-8 space-y-4">
                  {service.features.map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <Check className="text-[#C0C0C0] flex-shrink-0 mt-1" size={20} strokeWidth={2} />
                      <span className="text-[#E5E5E5] leading-relaxed">{feature}</span>
                    </div>
                  ))}
                </div>

                <a
                  href={`https://wa.me/5521967611301?text=Olá! Gostaria de saber mais sobre: ${service.title}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`flex items-center justify-center gap-2 rounded-full font-bold uppercase tracking-widest px-8 py-4 transition-all duration-300 ${
                    service.featured
                      ? 'bg-white text-black hover:scale-105 shadow-[0_0_20px_rgba(255,255,255,0.3)]'
                      : 'border border-white/20 bg-transparent text-white hover:bg-white hover:text-black'
                  }`}
                  data-testid={`pricing-cta-${index}`}
                >
                  <MessageCircle size={20} />
                  Agendar pelo WhatsApp
                </a>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-24 md:py-32 bg-[#121212]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div {...fadeUp} className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight uppercase mb-4">
              Como Funciona
            </h2>
            <p className="text-base md:text-lg font-light leading-relaxed tracking-wide text-[#A3A3A3] max-w-2xl mx-auto">
              Processo simples e eficiente para sua transformação
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              {
                step: '01',
                title: 'Contato Inicial',
                description: 'Entre em contato via WhatsApp e agende seu protocolo.',
              },
              {
                step: '02',
                title: 'Avaliação',
                description: 'Anamnese completa, avaliação física e definição de objetivos.',
              },
              {
                step: '03',
                title: 'Planejamento',
                description: 'Criação de estratégia nutricional e treino personalizados.',
              },
              {
                step: '04',
                title: 'Acompanhamento',
                description: 'Suporte contínuo e ajustes para garantir resultados.',
              },
            ].map((item, index) => (
              <motion.div
                key={item.step}
                {...fadeUp}
                transition={{ delay: index * 0.1 }}
                className="text-center"
                data-testid={`process-step-${index}`}
              >
                <div className="text-6xl font-black tracking-tighter text-[#333333] mb-4">
                  {item.step}
                </div>
                <h3 className="text-xl font-semibold tracking-normal mb-3">{item.title}</h3>
                <p className="text-[#A3A3A3] leading-relaxed">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 md:py-32">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div {...fadeUp} className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight uppercase mb-4">
              Dúvidas Frequentes
            </h2>
          </motion.div>

          <div className="space-y-6">
            {[
              {
                question: 'Qual a diferença entre presencial e online?',
                answer:
                  'O protocolo presencial inclui avaliação física com equipamentos (bioimpedância) e é ideal para quem prefere atendimento local. O protocolo online oferece acompanhamento completo remotamente, com plano alimentar e treino, sendo perfeito para quem busca flexibilidade.',
              },
              {
                question: 'Como funciona o acompanhamento online?',
                answer:
                  'Você recebe seu plano alimentar e treino personalizados, com suporte via WhatsApp para dúvidas. Fazemos ajustes estratégicos semanais baseados em seu progresso e reavaliações mensais completas.',
              },
              {
                question: 'Preciso de suplementos?',
                answer:
                  'A suplementação é individualizada. Avalio suas necessidades e, se necessário, prescrevo apenas o que realmente fará diferença para seus resultados. Não há obrigatoriedade.',
              },
              {
                question: 'Quanto tempo até ver resultados?',
                answer:
                  'Resultados iniciais aparecem em 2-4 semanas. Transformações significativas geralmente ocorrem em 8-12 semanas com adesão adequada ao protocolo.',
              },
            ].map((faq, index) => (
              <motion.div
                key={index}
                {...fadeUp}
                transition={{ delay: index * 0.1 }}
                className="bg-[#121212] border border-white/5 p-8"
                data-testid={`faq-${index}`}
              >
                <h3 className="text-xl font-semibold tracking-normal mb-4">{faq.question}</h3>
                <p className="text-[#A3A3A3] leading-relaxed">{faq.answer}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-[#121212]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div {...fadeUp}>
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight uppercase mb-6">
              Pronto para começar?
            </h2>
            <p className="text-base md:text-lg font-light leading-relaxed tracking-wide text-[#A3A3A3] mb-12">
              Entre em contato agora e dê o primeiro passo para sua transformação.
            </p>
            <a
              href="https://wa.me/5521967611301"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 rounded-full bg-white text-black font-bold uppercase tracking-widest px-8 py-4 hover:scale-105 transition-transform duration-300"
              data-testid="consultas-cta"
            >
              <MessageCircle size={20} />
              Falar no WhatsApp
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Consultas;